<script setup lang="ts">
import { Form as VeeForm } from 'vee-validate'
import type {
  InquiryAdditionalInformation,
  InquiryModalForm,
} from '#build/components'
import type { HouseDetails } from '~/types/house/types'
import type { PeriodDurationInquiry } from '~/types/inquiry/types'

const { house } = defineProps<{
  house: HouseDetails
  periods: PeriodDurationInquiry[]
}>()
defineEmits<{ close: [] }>()

const { query } = useRoute()
const {
  booking: pageBooking,
  bookedDates,
  bookedDatesExceptChecks,
  fetchBookings,
} = useHouseInquiryBooking()
const { currency, formattedPhone, user } = useUser()
const { booking, createRequest, setDates, setStep, updateRequest } =
  useHouseInquiryBooking(house.id)
const { trackEvent } = useTracking()

const formValues = reactive({
  email: user.value?.email ?? '',
  endDate: pageBooking.value.endDate
    ? new Date(pageBooking.value.endDate)
    : null,
  firstName: user.value?.firstName ?? '',
  lastName: user.value?.lastName ?? '',
  message: '',
  phone: formattedPhone.value,
  startDate: pageBooking.value.startDate
    ? new Date(pageBooking.value.startDate)
    : null,
})

const inquiryForm = ref<InstanceType<typeof InquiryModalForm> | null>(null)
const groupForm = ref<InstanceType<typeof InquiryAdditionalInformation> | null>(
  null,
)

const formIsValid = computed(() =>
  booking.value.currentStep === 0
    ? inquiryForm.value?.isFormValid
    : Object.values(groupForm.value?.group ?? {}).some((val) => val.count > 0),
)
const isCalendarOpen = computed(() =>
  Boolean(inquiryForm.value?.isCalendarOpen),
)

const onPeriodSelected = (
  period: { startDate: Date; endDate: Date } | null,
) => {
  if (period?.endDate && period?.startDate) {
    setDates(
      $dayjs(period.startDate).format('YYYY-MM-DD'),
      $dayjs(period.endDate).format('YYYY-MM-DD'),
    )
  } else {
    setDates(null, null)
  }
}
const onNextMonths = async (start: Date, end: Date) => {
  if (house.displayAvailabilities) {
    await fetchBookings(house.id, {
      end_date: end,
      private_token: query.privateToken?.toString(),
      start_date: start,
    })
  }
}

const submitForm = async (houseId: number) => {
  if (!formIsValid.value) return
  const event = `ask_question_${booking.value.requestUuid ? 'updated' : 'sent'}`
  const request = {
    budget: booking.value.price.value ?? 0,
    client_comment: formValues.message,
    email: formValues.email,
    end_date: $dayjs(formValues.endDate).format('YYYY-MM-DD'),
    first_name: formValues.firstName,
    house_id: houseId,
    inquiry_type: 'house_question' as const,
    last_name: formValues.lastName,
    phone: formValues.phone,
    start_date: $dayjs(formValues.startDate).format('YYYY-MM-DD'),
  }
  if (event === 'ask_question_sent') {
    await createRequest({
      currency: currency.value,
      request,
    })
  } else if (groupForm.value) {
    await updateRequest({
      number_of_adults: groupForm.value.group.adults.count,
      number_of_babies: groupForm.value.group.babies.count,
      number_of_children: groupForm.value.group.children.count,
    })
  }
  trackEvent({
    category: 'General',
    cluster_name: house.destination.cluster?.name,
    country_name: house.destination.country?.name,
    destination_name: house.destination.name,
    event,
    inquiry_type: 'house_question',
    product: {
      house_id: houseId,
      house_name: house.name,
      iconic: !!house.iconicCollection,
      online_booking_available: !!house.onlineReservation,
      price: booking.value.price.value || undefined,
    },
    source: 'product page',
  })
}

onMounted(async () => {
  setStep(0)
  if (house.displayAvailabilities && !bookedDates.value.length) {
    await fetchBookings(house.id, {
      end_date: formValues.endDate,
      private_token: query.privateToken?.toString(),
      start_date: formValues.startDate,
    })
  }
})

defineExpose({ formIsValid, isCalendarOpen, submitForm })
</script>

<template>
  <div
    :class="[
      'relative flex flex-col gap-4',
      isCalendarOpen ? 'z-fixed' : 'overflow-visible',
      { 'sm:min-h-[460px]': booking.currentStep === 0 },
    ]"
  >
    <VeeForm v-if="booking.currentStep === 0">
      <InquiryModalForm
        ref="inquiryForm"
        v-model="formValues"
        :booked-dates="bookedDates"
        :booked-dates-except-checks="bookedDatesExceptChecks"
        :house="house"
        :periods="periods"
        @next-months="onNextMonths"
        @period-selected="onPeriodSelected"
      />
    </VeeForm>

    <InquiryAdditionalInformation
      v-else-if="booking.currentStep === 1"
      ref="groupForm"
      hide-comment
    >
      <template #header>
        <p>
          {{ $t('account.sales.confirmationInfo2') }}
          <br />
          {{ $t('inquiry.giveMoreInfo') }}
        </p>
      </template>
    </InquiryAdditionalInformation>

    <InquiryModalSummary
      v-else-if="booking.currentStep === 2"
      @close="$emit('close')"
    />
  </div>
</template>
